import { useState, useEffect, useCallback, useRef } from 'react';
import { labProgressApi } from '../lib/api';

/**
 * Shared hook for lab mission progress persistence.
 * Loads saved progress on mount, saves each completion to backend.
 *
 * @param {string} labId - unique lab identifier (e.g. 'geometry', 'city-builder')
 * @param {number} totalMissions - total number of missions in the lab
 * @returns {{ completedMissions, unlockedMissions, currentMission, setCurrentMission,
 *            completeMission, xp, loading }}
 */
export function useLabProgress(labId, totalMissions = 10) {
  const [completedMissions, setCompletedMissions] = useState([]);
  const [unlockedMissions, setUnlockedMissions] = useState([0]);
  const [currentMission, setCurrentMission] = useState(0);
  const [xp, setXp] = useState(0);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  // Load saved progress on mount
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    labProgressApi.getStats()
      .then(data => {
        const labData = data?.per_lab?.[labId];
        if (labData) {
          const savedIds = (labData.completed_ids || []).map(Number);
          setCompletedMissions(savedIds);
          // Unlock completed missions + the next one
          const unlocked = [0];
          savedIds.forEach(id => {
            if (!unlocked.includes(id)) unlocked.push(id);
            if (id + 1 < totalMissions && !unlocked.includes(id + 1)) unlocked.push(id + 1);
          });
          setUnlockedMissions(unlocked.sort((a, b) => a - b));
          setXp(labData.xp || 0);
          // Set current to the first incomplete mission
          let next = 0;
          for (let i = 0; i < totalMissions; i++) {
            if (!savedIds.includes(i)) { next = i; break; }
            next = i;
          }
          setCurrentMission(next);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [labId, totalMissions]);

  // Complete a mission: update local state + persist to backend
  const completeMission = useCallback((missionIndex, opts = {}) => {
    const { stars = 1, xpEarned = 25, gameStats = null } = opts;
    setCompletedMissions(prev => {
      if (prev.includes(missionIndex)) return prev;
      return [...prev, missionIndex];
    });
    setUnlockedMissions(prev => {
      const next = missionIndex + 1;
      if (next < totalMissions && !prev.includes(next)) return [...prev, next];
      return prev;
    });
    setXp(prev => prev + xpEarned);

    // Persist to backend
    const payload = {
      lab_id: labId,
      mission_id: missionIndex,
      stars,
      xp_earned: xpEarned,
    };
    if (gameStats) payload.game_stats = gameStats;
    labProgressApi.completeMission(payload).catch(() => {});
  }, [labId, totalMissions]);

  return {
    completedMissions,
    unlockedMissions,
    currentMission,
    setCurrentMission,
    completeMission,
    xp,
    loading,
  };
}
